/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import { getScrollParent, isScrollable } from '@react-aria/utils';
import { type RefObject, useCallback, useEffect, useRef } from 'react';

const AUTOSCROLL_AREA_SIZE = 20;
const MAX_SCROLL_PX_PER_FRAME = 18;

interface AutoScrollAria {
    move(x: number, y: number): void;
    stop(): void;
}

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

function getScrollOrParent(element: Element | null) {
    if (!element) {
        return null;
    }
    return isScrollable(element) ? element : getScrollParent(element);
}

/**
 * Hook for automatic scrolling based on mouse position.
 * Scrolls an element when the cursor approaches its top or bottom edges.
 *
 * @param ref - A ref to the scrollable element or its parent
 * @returns An object with `move(x, y)` to update scroll velocity and `stop()` to cancel scrolling
 */
export function useAutoScroll(ref: RefObject<Element | null>): AutoScrollAria {
    const rafIdRef = useRef<number | null>(null);
    const velocityRef = useRef(0);

    const cancelLoop = useCallback(() => {
        if (rafIdRef.current == null) return;
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
    }, []);

    const startLoopIfNeeded = useCallback(() => {
        if (rafIdRef.current != null) return;

        function tick(): void {
            const element = getScrollOrParent(ref.current);
            if (!element) {
                cancelLoop();
                return;
            }

            const velocity = velocityRef.current;
            if (velocity === 0) {
                cancelLoop();
                return;
            }

            element.scrollTop += velocity;

            rafIdRef.current = requestAnimationFrame(tick);
        }

        rafIdRef.current = requestAnimationFrame(tick);
    }, [cancelLoop, ref]);

    const stop = useCallback((): void => {
        velocityRef.current = 0;
        cancelLoop();
    }, [cancelLoop]);

    const move = useCallback(
        (x: number, y: number): void => {
            void x;

            const element = getScrollOrParent(ref.current);
            if (!element) return;

            const rect = element.getBoundingClientRect();
            const topEdge = rect.top + AUTOSCROLL_AREA_SIZE;
            const bottomEdge = rect.bottom - AUTOSCROLL_AREA_SIZE;

            let nextVelocity = 0;

            if (y < topEdge) {
                const distance = clamp(topEdge - y, 0, AUTOSCROLL_AREA_SIZE);
                const ratio = distance / AUTOSCROLL_AREA_SIZE;
                nextVelocity = -Math.ceil(ratio * MAX_SCROLL_PX_PER_FRAME);
            } else if (y > bottomEdge) {
                const distance = clamp(y - bottomEdge, 0, AUTOSCROLL_AREA_SIZE);
                const ratio = distance / AUTOSCROLL_AREA_SIZE;
                nextVelocity = Math.ceil(ratio * MAX_SCROLL_PX_PER_FRAME);
            }

            if (nextVelocity === 0) {
                velocityRef.current = 0;
                cancelLoop();
                return;
            }

            velocityRef.current = nextVelocity;
            startLoopIfNeeded();
        },
        [ref, cancelLoop, startLoopIfNeeded]
    );

    useEffect(() => {
        return () => cancelLoop();
    }, [cancelLoop]);

    return { move, stop };
}
